import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { settingsApi, userApi, inquiryApi, emailConfigApi, formatApiError } from '../services/api';
import { useAuth } from '../context/AuthContext';
import EmailTemplatesManager from '../components/EmailTemplatesManager';
import { emailDocument } from '../components/EmailReader';
import NumberFormatsCard from '../components/NumberFormatsCard';
import PhotoSlotsCard from '../components/PhotoSlotsCard';
import CleaningChecklistCard from '../components/CleaningChecklistCard';
import AccountantAccessCard from '../components/AccountantAccessCard';
import EmployeeAccessCard from '../components/EmployeeAccessCard';
import AutoLetterRulesCard from '../components/AutoLetterRulesCard';
import BackupStatusCard from '../components/BackupStatusCard';
import DamageCatalogCard from '../components/DamageCatalogCard';
import { 
  Gear, Buildings, Upload, Users, Trash, Plus, FileText, Key, Copy, EnvelopeSimple, CheckCircle, XCircle 
} from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const Settings = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUserModal, setShowUserModal] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    name: '',
    role: 'employee'
  });
  const [contractTerms, setContractTerms] = useState('');
  const [savingTerms, setSavingTerms] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [regeneratingKey, setRegeneratingKey] = useState(false);
  // Email config
  const [emailConfig, setEmailConfig] = useState(null);
  const [emailDirtyFields, setEmailDirtyFields] = useState({});
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailTesting, setEmailTesting] = useState(false);
  const [emailTestResult, setEmailTestResult] = useState(null);

  // AGB / Mietbedingungen als PDF
  const [contractTermsSource, setContractTermsSource] = useState('text');
  const [contractTermsPdf, setContractTermsPdf] = useState(null);
  const [contractTermsPdfUploading, setContractTermsPdfUploading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [settingsRes, usersRes, emailRes] = await Promise.all([
        settingsApi.get(),
        user?.role === 'admin' ? userApi.getAll() : Promise.resolve({ data: [] }),
        user?.role === 'admin' ? emailConfigApi.get() : Promise.resolve({ data: null }),
      ]);
      setSettings(settingsRes.data);
      setContractTerms(settingsRes.data?.contract_terms || '');
      setContractTermsSource(settingsRes.data?.contract_terms_source || 'text');
      setContractTermsPdf(
        settingsRes.data?.contract_terms_pdf
          ? {
              path: settingsRes.data.contract_terms_pdf,
              name: settingsRes.data.contract_terms_pdf_name,
              uploaded_at: settingsRes.data.contract_terms_pdf_uploaded_at,
            }
          : null
      );
      setContractTermsSource(settingsRes.data?.contract_terms_source || 'text');
      setContractTermsPdf(
        settingsRes.data?.contract_terms_pdf
          ? {
              path: settingsRes.data.contract_terms_pdf,
              name: settingsRes.data.contract_terms_pdf_name || 'AGB.pdf',
              uploaded_at: settingsRes.data.contract_terms_pdf_uploaded_at,
            }
          : null
      );
      setUsers(usersRes.data);
      setEmailConfig(emailRes.data || null);
      setEmailDirtyFields({});
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleEmailField = (field, value) => {
    setEmailConfig((prev) => ({ ...(prev || {}), [field]: value }));
    setEmailDirtyFields((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveEmail = async () => {
    if (Object.keys(emailDirtyFields).length === 0) {
      toast.info('Keine Änderungen vorhanden');
      return;
    }

    setEmailSaving(true);
    try {
      // Nur tatsächlich geänderte Felder speichern.
      // Dadurch können alte Werte im Frontend keine IMAP/SMTP-Daten überschreiben.
      const payload = { ...emailDirtyFields };

      if (payload.imap_password === '••••••••') delete payload.imap_password;
      if (payload.smtp_password === '••••••••') delete payload.smtp_password;

      const res = await emailConfigApi.update(payload);
      setEmailConfig(res.data);
      setEmailDirtyFields({});
      toast.success('E-Mail-Konfiguration gespeichert');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEmailSaving(false);
    }
  };

  const handleTestEmail = async () => {
    setEmailTesting(true);
    setEmailTestResult(null);
    try {
      const res = await emailConfigApi.test();
      setEmailTestResult(res.data);
      const ok = res.data?.imap?.ok && res.data?.smtp?.ok;
      if (ok) toast.success('Verbindung erfolgreich — IMAP und SMTP funktionieren!');
      else toast.error('Verbindungstest fehlgeschlagen — Details siehe unten');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEmailTesting(false);
    }
  };

  const handleSettingsUpdate = async (field, value) => {
    try {
      const response = await settingsApi.update({ [field]: value });
      setSettings(response.data);
      toast.success('Einstellungen gespeichert');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      await settingsApi.uploadLogo(file);
      toast.success('Logo hochgeladen');
      loadData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleContractTermsPdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Bitte eine PDF-Datei auswählen');
      e.target.value = '';
      return;
    }

    setContractTermsPdfUploading(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/api/settings/contract-terms/pdf`,
        formData,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );

      setContractTermsPdf(response.data);
      setContractTermsSource('pdf');
      setSettings((prev) => ({
        ...(prev || {}),
        contract_terms_source: 'pdf',
        contract_terms_pdf: response.data.path,
        contract_terms_pdf_name: response.data.filename,
        contract_terms_pdf_uploaded_at: response.data.uploaded_at,
      }));

      toast.success('AGB-PDF hochgeladen und als Quelle ausgewählt');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setContractTermsPdfUploading(false);
      e.target.value = '';
    }
  };

  const handleContractTermsSourceChange = async (source) => {
    setContractTermsSource(source);
    try {
      const response = await settingsApi.update({
        contract_terms_source: source,
      });
      setSettings(response.data);
      toast.success(source === 'pdf' ? 'PDF-AGB ausgewählt' : 'Text-AGB ausgewählt');
    } catch (error) {
      setContractTermsSource(contractTermsSource);
      toast.error(formatApiError(error));
    }
  };

  const saveContractTerms = async () => {
    setSavingTerms(true);
    try {
      const response = await settingsApi.update({ contract_terms: contractTerms });
      setSettings(response.data);
      toast.success('Mietbedingungen gespeichert');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSavingTerms(false);
    }
  };

  const loadDefaultContractTerms = async () => {
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const res = await axios.get(`${API}/api/settings/contract-terms/default`, { withCredentials: true });
      setContractTerms(res.data.contract_terms || '');
      toast.info('Standard-Mietbedingungen geladen – speichern nicht vergessen');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const resetContractTerms = async () => {
    if (!window.confirm('Mietbedingungen auf Standard zurücksetzen? (Das Feld wird geleert – der Standardtext wird wieder beim PDF verwendet.)')) return;
    try {
      const response = await settingsApi.update({ contract_terms: '' });
      setSettings(response.data);
      setContractTerms('');
      toast.success('Mietbedingungen zurückgesetzt');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleRegenerateApiKey = async () => {
    if (settings?.public_api_key) {
      if (!window.confirm('Achtung: Der alte API-Key wird sofort ungültig! Alle Integrationen müssen aktualisiert werden. Fortfahren?')) return;
    }
    setRegeneratingKey(true);
    try {
      const res = await inquiryApi.regenerateApiKey();
      setSettings({ ...settings, public_api_key: res.data.public_api_key });
      setShowApiKey(true);
      toast.success('Neuer API-Key erstellt');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setRegeneratingKey(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('In Zwischenablage kopiert');
    } catch {
      toast.error('Kopieren fehlgeschlagen');
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${API}/api/auth/register`, newUser, { withCredentials: true });
      toast.success('Benutzer erstellt');
      setShowUserModal(false);
      setNewUser({ email: '', password: '', name: '', role: 'employee' });
      loadData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Benutzer wirklich löschen?')) return;
    try {
      await userApi.delete(userId);
      toast.success('Benutzer gelöscht');
      loadData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="h-8 w-48 skeleton rounded mb-6" />
        <div className="space-y-6">
          <div className="card-swiss h-64 skeleton" />
          <div className="card-swiss h-48 skeleton" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-[#111418]">Einstellungen</h1>
        <p className="text-[#525866] mt-1">Systemkonfiguration verwalten</p>
      </div>

      {/* Company Settings */}
      <div className="card-swiss p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-[#0A0A0A]/10 rounded-sm flex items-center justify-center">
            <Buildings weight="fill" size={20} className="text-[#0A0A0A]" />
          </div>
          <h2 className="text-lg font-bold">Firmendaten</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label className="overline">Firmenname</Label>
            <Input
              value={settings?.company_name || ''}
              onChange={(e) => handleSettingsUpdate('company_name', e.target.value)}
              className="input-swiss"
              data-testid="company-name-input"
            />
          </div>
          <div>
            <Label className="overline">E-Mail</Label>
            <Input
              type="email"
              value={settings?.company_email || ''}
              onChange={(e) => handleSettingsUpdate('company_email', e.target.value)}
              className="input-swiss"
            />
          </div>
          <div>
            <Label className="overline">Telefon</Label>
            <Input
              value={settings?.company_phone || ''}
              onChange={(e) => handleSettingsUpdate('company_phone', e.target.value)}
              className="input-swiss"
            />
          </div>
          <div>
            <Label className="overline">Adresse</Label>
            <Input
              value={settings?.company_address || ''}
              onChange={(e) => handleSettingsUpdate('company_address', e.target.value)}
              className="input-swiss"
            />
          </div>
          <div>
            <Label className="overline">PLZ</Label>
            <Input
              value={settings?.company_postal_code || ''}
              onChange={(e) => handleSettingsUpdate('company_postal_code', e.target.value)}
              className="input-swiss"
            />
          </div>
          <div>
            <Label className="overline">Stadt</Label>
            <Input
              value={settings?.company_city || ''}
              onChange={(e) => handleSettingsUpdate('company_city', e.target.value)}
              className="input-swiss"
            />
          </div>
        </div>

        {/* Logo Upload */}
        <div className="mt-6 pt-6 border-t border-[#E2E4E9]">
          <Label className="overline block mb-2">Firmenlogo</Label>
          <div className="flex items-center gap-4">
            {settings?.logo_url && (
              <div className="w-24 h-24 border border-[#E2E4E9] rounded-sm overflow-hidden">
                <img 
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${settings.logo_url}`}
                  alt="Logo"
                  className="w-full h-full object-contain"
                />
              </div>
            )}
            <label className="btn-outline px-4 py-2 cursor-pointer flex items-center gap-2">
              <Upload weight="bold" size={16} />
              Logo hochladen
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
                data-testid="logo-upload-input"
              />
            </label>
          </div>
        </div>
      </div>

      {/* Invoice Settings */}
      <div className="card-swiss p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-[#E60000]/10 rounded-sm flex items-center justify-center">
            <Gear weight="fill" size={20} className="text-[#E60000]" />
          </div>
          <h2 className="text-lg font-bold">Rechnungseinstellungen</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <Label className="overline">Rechnungspräfix</Label>
            <Input
              value={settings?.invoice_prefix || 'RG'}
              onChange={(e) => handleSettingsUpdate('invoice_prefix', e.target.value)}
              className="input-swiss"
            />
          </div>
          <div>
            <Label className="overline">MwSt.-Satz (%)</Label>
            <Input
              type="number"
              value={settings?.tax_rate || 19}
              onChange={(e) => handleSettingsUpdate('tax_rate', parseFloat(e.target.value))}
              className="input-swiss"
              min={0}
              max={100}
              step={0.1}
            />
          </div>
          <div>
            <Label className="overline">Währung</Label>
            <Select 
              value={settings?.currency || 'EUR'} 
              onValueChange={(v) => handleSettingsUpdate('currency', v)}
            >
              <SelectTrigger className="input-swiss">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="EUR">EUR (€)</SelectItem>
                <SelectItem value="CHF">CHF (Fr.)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Number Formats / Nummernkreise */}
      <NumberFormatsCard />

      {/* Foto-Slots Übergabe/Rückgabe */}
      <PhotoSlotsCard />

      {/* Reinigungs-Checkliste */}
      <CleaningChecklistCard />

      {/* Buchhalter / Steuerberater-Zugänge */}
      <AccountantAccessCard currentUserRole={user?.role} />

      {/* Mitarbeiter-Zugänge mit granularen Rechten */}
      <EmployeeAccessCard currentUserRole={user?.role} />

      {/* Automatisierungs-Regeln für Auto-Briefe */}
      <AutoLetterRulesCard />

      {/* Schadenskatalog */}
      {user?.role === 'admin' && <BackupStatusCard />}
      <DamageCatalogCard />

      {/* Bank Details */}
      <div className="card-swiss p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-[#009951]/10 rounded-sm flex items-center justify-center">
            <Gear weight="fill" size={20} className="text-[#009951]" />
          </div>
          <div>
            <h2 className="text-lg font-bold">Bankverbindung</h2>
            <p className="text-sm text-[#525866]">Wird automatisch auf Rechnungen im Zahlungshinweis angezeigt</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label className="overline">Kontoinhaber</Label>
            <Input
              value={settings?.bank_account_holder || ''}
              onChange={(e) => handleSettingsUpdate('bank_account_holder', e.target.value)}
              className="input-swiss"
              placeholder="z.B. AutoVermietung GmbH"
              data-testid="bank-holder-input"
            />
          </div>
          <div>
            <Label className="overline">Bankname</Label>
            <Input
              value={settings?.bank_name || ''}
              onChange={(e) => handleSettingsUpdate('bank_name', e.target.value)}
              className="input-swiss"
              placeholder="z.B. Sparkasse Berlin"
              data-testid="bank-name-input"
            />
          </div>
          <div>
            <Label className="overline">IBAN</Label>
            <Input
              value={settings?.bank_iban || ''}
              onChange={(e) => handleSettingsUpdate('bank_iban', e.target.value.toUpperCase())}
              className="input-swiss"
              placeholder="z.B. DE89 3704 0044 0532 0130 00"
              data-testid="bank-iban-input"
            />
          </div>
          <div>
            <Label className="overline">BIC</Label>
            <Input
              value={settings?.bank_bic || ''}
              onChange={(e) => handleSettingsUpdate('bank_bic', e.target.value.toUpperCase())}
              className="input-swiss"
              placeholder="z.B. COBADEFFXXX"
              data-testid="bank-bic-input"
            />
          </div>
        </div>
      </div>

      {/* Tax Info */}
      <div className="card-swiss p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-[#0A0A0A]/10 rounded-sm flex items-center justify-center">
            <Gear weight="fill" size={20} className="text-[#0A0A0A]" />
          </div>
          <div>
            <h2 className="text-lg font-bold">Steuerliche Angaben</h2>
            <p className="text-sm text-[#525866]">Erscheint im Footer aller Dokumente (Rechnungen, Verträge, Quittungen)</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label className="overline">Steuernummer</Label>
            <Input
              value={settings?.tax_id || ''}
              onChange={(e) => handleSettingsUpdate('tax_id', e.target.value)}
              className="input-swiss"
              placeholder="z.B. 27/123/45678"
              data-testid="tax-id-input"
            />
          </div>
          <div>
            <Label className="overline">USt-IdNr.</Label>
            <Input
              value={settings?.vat_id || ''}
              onChange={(e) => handleSettingsUpdate('vat_id', e.target.value.toUpperCase())}
              className="input-swiss"
              placeholder="z.B. DE123456789"
              data-testid="vat-id-input"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="overline">Geschäftsführer / Inhaber</Label>
            <Input
              value={settings?.managing_director || ''}
              onChange={(e) => handleSettingsUpdate('managing_director', e.target.value)}
              className="input-swiss"
              placeholder="z.B. Max Mustermann"
              data-testid="managing-director-input"
            />
          </div>
        </div>
      </div>

      {/* E-Mail-Konto (IMAP / SMTP) */}
      {user?.role === 'admin' && (
      <div className="card-swiss p-6" data-testid="email-config-card">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-[#E60000]/10 rounded-sm flex items-center justify-center">
            <EnvelopeSimple weight="fill" size={20} className="text-[#E60000]" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-bold">E-Mail-Konto (IMAP / SMTP)</h2>
            <p className="text-sm text-[#525866]">
              Rufe Kunden-E-Mails aus deinem Postfach ab und antworte direkt aus der App.
              Für All-Inkl: IMAP-Server & SMTP-Server in der KAS-Verwaltung → „E-Mail-Konten".
            </p>
          </div>
          {emailConfig?.last_poll_at && (
            <span className="text-xs text-[#8A94A6] whitespace-nowrap">
              Letzter Abruf: {new Date(emailConfig.last_poll_at).toLocaleString('de-DE')}
            </span>
          )}
        </div>

        <div className="space-y-6">
          {/* IMAP */}
          <div>
            <h3 className="font-semibold text-[#111418] mb-2 text-sm uppercase tracking-wider">Posteingang (IMAP)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Label className="overline">IMAP-Server</Label>
                <Input
                  value={emailConfig?.imap_host || ''}
                  onChange={(e) => handleEmailField('imap_host', e.target.value)}
                  placeholder="z.B. w01xxxxxx.kasserver.com"
                  data-testid="imap-host-input"
                />
              </div>
              <div>
                <Label className="overline">Port</Label>
                <Input
                  type="number"
                  value={emailConfig?.imap_port ?? 993}
                  onChange={(e) => handleEmailField('imap_port', parseInt(e.target.value, 10) || 993)}
                  placeholder="993"
                  data-testid="imap-port-input"
                />
              </div>
              <div>
                <Label className="overline">SSL</Label>
                <Select
                  value={String(emailConfig?.imap_ssl ?? true)}
                  onValueChange={(v) => handleEmailField('imap_ssl', v === 'true')}
                >
                  <SelectTrigger data-testid="imap-ssl-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">SSL/TLS (empfohlen)</SelectItem>
                    <SelectItem value="false">Keine Verschlüsselung</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline">Benutzername</Label>
                <Input
                  value={emailConfig?.imap_user || ''}
                  onChange={(e) => handleEmailField('imap_user', e.target.value)}
                  placeholder="info@t-rent24.de"
                  data-testid="imap-user-input"
                />
              </div>
              <div>
                <Label className="overline">Passwort</Label>
                <Input
                  type="password"
                  value={emailConfig?.imap_password || ''}
                  onChange={(e) => handleEmailField('imap_password', e.target.value)}
                  placeholder="Neues Passwort (leer lassen um zu behalten)"
                  data-testid="imap-password-input"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="overline">Ordner</Label>
                <Input
                  value={emailConfig?.folder || 'INBOX'}
                  onChange={(e) => handleEmailField('folder', e.target.value)}
                  placeholder="INBOX"
                  data-testid="imap-folder-input"
                />
              </div>
            </div>
          </div>

          {/* SMTP */}
          <div>
            <h3 className="font-semibold text-[#111418] mb-2 text-sm uppercase tracking-wider">Postausgang (SMTP)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Label className="overline">SMTP-Server</Label>
                <Input
                  value={emailConfig?.smtp_host || ''}
                  onChange={(e) => handleEmailField('smtp_host', e.target.value)}
                  placeholder="z.B. w01xxxxxx.kasserver.com"
                  data-testid="smtp-host-input"
                />
              </div>
              <div>
                <Label className="overline">Port</Label>
                <Input
                  type="number"
                  value={emailConfig?.smtp_port ?? 465}
                  onChange={(e) => handleEmailField('smtp_port', parseInt(e.target.value, 10) || 465)}
                  placeholder="465"
                  data-testid="smtp-port-input"
                />
              </div>
              <div>
                <Label className="overline">SSL</Label>
                <Select
                  value={String(emailConfig?.smtp_ssl ?? true)}
                  onValueChange={(v) => handleEmailField('smtp_ssl', v === 'true')}
                >
                  <SelectTrigger data-testid="smtp-ssl-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">SSL/TLS (Port 465)</SelectItem>
                    <SelectItem value="false">STARTTLS / Plain (Port 587)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline">Benutzername</Label>
                <Input
                  value={emailConfig?.smtp_user || ''}
                  onChange={(e) => handleEmailField('smtp_user', e.target.value)}
                  placeholder="info@t-rent24.de"
                  data-testid="smtp-user-input"
                />
              </div>
              <div>
                <Label className="overline">Passwort</Label>
                <Input
                  type="password"
                  value={emailConfig?.smtp_password || ''}
                  onChange={(e) => handleEmailField('smtp_password', e.target.value)}
                  placeholder="Neues Passwort (leer lassen um zu behalten)"
                  data-testid="smtp-password-input"
                />
              </div>
              <div>
                <Label className="overline">Absender-Adresse</Label>
                <Input
                  value={emailConfig?.from_email || ''}
                  onChange={(e) => handleEmailField('from_email', e.target.value)}
                  placeholder="info@t-rent24.de"
                  data-testid="from-email-input"
                />
              </div>
              <div>
                <Label className="overline">Absender-Name</Label>
                <Input
                  value={emailConfig?.from_name || ''}
                  onChange={(e) => handleEmailField('from_name', e.target.value)}
                  placeholder="T-Rent24 Autovermietung"
                  data-testid="from-name-input"
                />
              </div>
            </div>
          </div>

          {/* E-Mail-Signatur */}
          <div className="border-t border-[#E2E4E9] pt-6">
            <h3 className="font-semibold text-[#111418] mb-2 text-sm uppercase tracking-wider">
              E-Mail-Signatur
            </h3>
            <p className="text-sm text-[#525866] mb-3">
              Diese Signatur wird automatisch an neue E-Mails angehängt.
              HTML kann verwendet werden.
            </p>

            <Label className="overline">Signatur</Label>
            <textarea
              value={emailConfig?.signature_html || ''}
              onChange={(e) => handleEmailField('signature_html', e.target.value)}
              rows={10}
              className="w-full mt-1 px-3 py-2 border border-[#E2E4E9] rounded-sm bg-white text-sm font-mono resize-y focus:outline-none focus:ring-2 focus:ring-[#111418]/20"
              placeholder={'<strong>Max Mustermann</strong>\n<br>Geschäftsführer\n<br>Firma GmbH'}
              data-testid="email-signature-input"
            />

            {emailConfig?.signature_html && (
              <div className="mt-4">
                <Label className="overline">Vorschau</Label>
                <iframe
                  title="Signaturvorschau"
                  sandbox=""
                  referrerPolicy="no-referrer"
                  srcDoc={emailDocument(emailConfig.signature_html)}
                  className="mt-1 w-full h-48 bg-white border border-[#E2E4E9] rounded-sm"
                />
              </div>
            )}
          </div>

          {/* Auto-polling */}
          <div className="flex flex-col md:flex-row md:items-end gap-3 p-3 bg-[#F7F8FA] border border-[#E2E4E9] rounded-sm">
            <div className="flex-1">
              <Label className="overline">Automatischer Abruf</Label>
              <Select
                value={String(emailConfig?.auto_poll_enabled ?? false)}
                onValueChange={(v) => handleEmailField('auto_poll_enabled', v === 'true')}
              >
                <SelectTrigger data-testid="auto-poll-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="false">Nur manuell ("Posteingang abrufen"-Button)</SelectItem>
                  <SelectItem value="true">Automatisch im Hintergrund</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-full md:w-40">
              <Label className="overline">Intervall (Min.)</Label>
              <Input
                type="number" min={1}
                value={emailConfig?.poll_interval_minutes ?? 5}
                onChange={(e) => handleEmailField('poll_interval_minutes', parseInt(e.target.value, 10) || 5)}
                data-testid="poll-interval-input"
              />
            </div>
          </div>

          {emailConfig?.last_error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-sm text-sm text-[#8b0000]">
              <strong>Letzter Fehler:</strong> {emailConfig.last_error}
            </div>
          )}

          {/* Test results */}
          {emailTestResult && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {['imap', 'smtp'].map((k) => {
                const r = emailTestResult[k];
                return (
                  <div
                    key={k}
                    className={`p-3 border rounded-sm text-sm ${r?.ok ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}
                    data-testid={`test-result-${k}`}
                  >
                    <div className="flex items-center gap-2 font-semibold">
                      {r?.ok ? <CheckCircle size={14} className="text-[#009951]" /> : <XCircle size={14} className="text-[#E60000]" />}
                      {k.toUpperCase()}: {r?.ok ? 'OK' : 'Fehler'}
                    </div>
                    {r?.error && <p className="text-xs mt-1">{r.error}</p>}
                    {r?.ok && r?.inbox_count != null && <p className="text-xs mt-1">Posteingang: {r.inbox_count} E-Mail(s)</p>}
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex gap-3">
            <Button onClick={handleSaveEmail} disabled={emailSaving} className="btn-primary" data-testid="save-email-config-btn">
              {emailSaving ? 'Speichere…' : 'Speichern'}
            </Button>
            <Button onClick={handleTestEmail} disabled={emailTesting} variant="outline" data-testid="test-email-config-btn">
              {emailTesting ? 'Teste…' : 'Verbindung testen'}
            </Button>
          </div>
        </div>
      </div>
      )}

      {/* E-Mail-Vorlagen */}
      {user?.role === 'admin' && <EmailTemplatesManager />}

      {/* Homepage-Anfragen API-Key */}
      <div className="card-swiss p-6" data-testid="api-key-card">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-[#E60000]/10 rounded-sm flex items-center justify-center">
            <Key weight="fill" size={20} className="text-[#E60000]" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-bold">Homepage-Anfragen (API-Key)</h2>
            <p className="text-sm text-[#525866]">
              Integriere ein Anfrageformular auf deiner Homepage. Anfragen landen direkt im Menüpunkt <strong>„Anfragen"</strong>.
            </p>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <Label className="overline">Dein API-Key</Label>
            {settings?.public_api_key ? (
              <div className="mt-1 flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-[#F7F8FA] border border-[#E2E4E9] rounded-sm text-xs font-mono break-all" data-testid="api-key-display">
                  {showApiKey ? settings.public_api_key : '•'.repeat(Math.min(settings.public_api_key.length, 40))}
                </code>
                <Button type="button" variant="outline" size="sm" onClick={() => setShowApiKey(!showApiKey)}>
                  {showApiKey ? 'Verbergen' : 'Anzeigen'}
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => copyToClipboard(settings.public_api_key)} data-testid="copy-api-key-btn">
                  <Copy weight="bold" size={14} />
                </Button>
              </div>
            ) : (
              <p className="mt-1 text-sm text-[#8A94A6]">Noch kein API-Key erstellt.</p>
            )}
            <div className="mt-2">
              <Button
                onClick={handleRegenerateApiKey}
                variant="outline"
                size="sm"
                disabled={regeneratingKey}
                className={settings?.public_api_key ? 'text-[#E60000] border-[#E60000] hover:bg-red-50' : ''}
                data-testid="regenerate-api-key-btn"
              >
                {regeneratingKey ? 'Erstelle…' : settings?.public_api_key ? 'Neuen Key generieren' : 'API-Key erstellen'}
              </Button>
            </div>
          </div>

          {settings?.public_api_key && (
            <div>
              <Label className="overline">Code-Beispiel für deine Homepage</Label>
              <p className="text-xs text-[#8A94A6] mb-2">
                POST-Endpoint: <code className="bg-[#F7F8FA] px-1 rounded">{(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api/public/inquiry</code>
              </p>
              <div className="relative">
                <pre className="bg-[#111418] text-[#e7e9ea] p-4 rounded-sm text-xs overflow-x-auto font-mono" data-testid="code-example">
{`<form id="inquiryForm">
  <input name="first_name" placeholder="Vorname" required />
  <input name="last_name" placeholder="Nachname" required />
  <input name="email" type="email" placeholder="E-Mail" required />
  <input name="phone" placeholder="Telefon" />
  <input name="vehicle_preference" placeholder="Wunschfahrzeug" />
  <input name="start_date" type="date" />
  <input name="end_date" type="date" />
  <textarea name="message" placeholder="Nachricht"></textarea>
  <button type="submit">Anfrage senden</button>
</form>

<script>
document.getElementById('inquiryForm').addEventListener('submit', async (e) => {
  e.preventDefault();
  const data = Object.fromEntries(new FormData(e.target));
  data.source = 'homepage';
  const res = await fetch('${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api/public/inquiry', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': '${settings.public_api_key}'
    },
    body: JSON.stringify(data)
  });
  if (res.ok) {
    alert('Danke! Wir melden uns kurzfristig bei Ihnen.');
    e.target.reset();
  } else {
    alert('Fehler beim Senden.');
  }
});
</script>`}
                </pre>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="absolute top-2 right-2"
                  onClick={() => copyToClipboard(document.querySelector('[data-testid="code-example"]').textContent)}
                >
                  <Copy weight="bold" size={14} />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mietbedingungen / Contract Terms Editor */}
      <div className="card-swiss p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-[#E60000]/10 rounded-sm flex items-center justify-center">
            <FileText weight="fill" size={20} className="text-[#E60000]" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-bold">Mietbedingungen (Vertragstext)</h2>
            <p className="text-sm text-[#525866]">
              Wird auf allen Mietverträgen als Anlage angedruckt. Leer lassen = Standard-Mietbedingungen.
              Jede Sektion startet mit einer Zeile <code className="bg-[#F7F8FA] px-1 rounded">#&nbsp;Überschrift</code>, darunter der Text in Absätzen.
            </p>
          </div>
        </div>
        <div className="space-y-3">

          {/* AGB-Quelle */}
          <div className="p-4 border border-[#E2E4E9] rounded-sm bg-[#F7F8FA]">
            <Label className="overline mb-3 block">AGB im Mietvertrag verwenden</Label>

            <div className="flex flex-col gap-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="contract-terms-source"
                  value="text"
                  checked={contractTermsSource === 'text'}
                  onChange={() => handleContractTermsSourceChange('text')}
                  className="mt-1 h-4 w-4"
                  data-testid="contract-terms-source-text"
                />
                <div>
                  <div className="font-medium text-[#111418]">
                    AGB als Vertragstext
                  </div>
                  <div className="text-xs text-[#8A94A6]">
                    Der unten eingetragene Text wird in den Mietvertrag übernommen.
                  </div>
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="contract-terms-source"
                  value="pdf"
                  checked={contractTermsSource === 'pdf'}
                  onChange={() => handleContractTermsSourceChange('pdf')}
                  className="mt-1 h-4 w-4"
                  data-testid="contract-terms-source-pdf"
                />
                <div>
                  <div className="font-medium text-[#111418]">
                    AGB als PDF-Anlage
                  </div>
                  <div className="text-xs text-[#8A94A6]">
                    Die hochgeladene PDF wird unverändert als Anlage zum Mietvertrag verwendet.
                  </div>
                </div>
              </label>
            </div>

            <div className="mt-4 pt-4 border-t border-[#E2E4E9]">
              <input
                id="contract-terms-pdf-upload"
                type="file"
                accept="application/pdf,.pdf"
                onChange={handleContractTermsPdfUpload}
                className="hidden"
                disabled={contractTermsPdfUploading}
              />

              <label
                htmlFor="contract-terms-pdf-upload"
                className="inline-flex items-center justify-center px-4 py-2 border border-[#E2E4E9] bg-white rounded-sm cursor-pointer hover:bg-[#F7F8FA] text-sm font-medium"
              >
                {contractTermsPdfUploading
                  ? 'PDF wird hochgeladen…'
                  : contractTermsPdf
                    ? 'AGB-PDF ersetzen'
                    : 'AGB-PDF hochladen'}
              </label>

              {contractTermsPdf && (
                <div className="mt-3 text-sm">
                  <div className="font-medium text-[#111418]">
                    Aktuelle PDF: {contractTermsPdf.name}
                  </div>
                  {contractTermsPdf.uploaded_at && (
                    <div className="text-xs text-[#8A94A6] mt-1">
                      Hochgeladen: {new Date(contractTermsPdf.uploaded_at).toLocaleString('de-DE')}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={loadDefaultContractTerms}
              data-testid="load-default-terms-btn"
            >
              Standardtext laden
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={resetContractTerms}
              className="text-[#E60000] border-[#E60000] hover:bg-red-50"
            >
              Auf Standard zurücksetzen
            </Button>
          </div>
          <textarea
            value={contractTerms}
            onChange={(e) => setContractTerms(e.target.value)}
            className="input-swiss w-full px-3 py-2 font-mono text-xs"
            rows={18}
            placeholder={`# § 1 Mietgegenstand
Der Vermieter überlässt dem Mieter das im Mietvertrag bezeichnete Fahrzeug...

# § 2 Mietzeit
Die Mietzeit beginnt mit der Übernahme des Fahrzeugs...`}
            data-testid="contract-terms-textarea"
          />
          <div className="flex justify-end">
            <Button
              onClick={saveContractTerms}
              className="btn-primary"
              disabled={savingTerms}
              data-testid="save-contract-terms-btn"
            >
              {savingTerms ? 'Speichere…' : 'Mietbedingungen speichern'}
            </Button>
          </div>
        </div>
      </div>

      {/* User Management - Admin only */}
      {user?.role === 'admin' && (
        <div className="card-swiss p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#0A0A0A]/10 rounded-sm flex items-center justify-center">
                <Users weight="fill" size={20} className="text-[#0A0A0A]" />
              </div>
              <h2 className="text-lg font-bold">Benutzerverwaltung</h2>
            </div>
            <Button onClick={() => setShowUserModal(true)} className="btn-primary" data-testid="add-user-btn">
              <Plus weight="bold" size={16} className="mr-2" />
              Benutzer hinzufügen
            </Button>
          </div>

          <table className="table-swiss">
            <thead>
              <tr>
                <th>Name</th>
                <th>E-Mail</th>
                <th>Rolle</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td className="font-medium">{u.name}</td>
                  <td className="text-[#525866]">{u.email}</td>
                  <td>
                    <span className={`badge ${u.role === 'admin' ? 'badge-error' : 'badge-neutral'}`}>
                      {u.role === 'admin' ? 'Admin' : 'Mitarbeiter'}
                    </span>
                  </td>
                  <td>
                    {u.id !== user?.id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteUser(u.id)}
                        className="text-[#E60000] hover:bg-red-50"
                      >
                        <Trash weight="bold" size={16} />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Integrationen */}
      <div className="card-swiss p-6" data-testid="integrations-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-[#111418]">Integrationen</h2>
            <p className="text-sm text-[#525866]">
              Externe Dienste und Schnittstellen verwalten
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => navigate('/settings/integrations/whatsapp')}
            className="text-left p-4 border border-[#E2E4E9] rounded-md hover:border-[#25D366] hover:bg-[#F7FEF9] transition flex items-center gap-3"
            data-testid="nav-integrations-whatsapp"
          >
            <div className="w-10 h-10 rounded bg-[#25D366] flex items-center justify-center text-white font-bold">
              W
            </div>
            <div className="flex-1">
              <p className="font-semibold text-[#111418]">WhatsApp Business</p>
              <p className="text-xs text-[#525866]">Meta Cloud API · Chat mit Kunden</p>
            </div>
            <span className="text-[#8A94A6]">→</span>
          </button>
        </div>
      </div>

      {/* Add User Modal */}
      <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Neuen Benutzer hinzufügen</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddUser} className="space-y-4">
            <div>
              <Label className="overline">Name</Label>
              <Input
                value={newUser.name}
                onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                className="input-swiss"
                required
                data-testid="new-user-name-input"
              />
            </div>
            <div>
              <Label className="overline">E-Mail</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                className="input-swiss"
                required
                data-testid="new-user-email-input"
              />
            </div>
            <div>
              <Label className="overline">Passwort</Label>
              <Input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                className="input-swiss"
                required
                minLength={6}
                data-testid="new-user-password-input"
              />
            </div>
            <div>
              <Label className="overline">Rolle</Label>
              <Select 
                value={newUser.role} 
                onValueChange={(v) => setNewUser({...newUser, role: v})}
              >
                <SelectTrigger className="input-swiss">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="employee">Mitarbeiter</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowUserModal(false)}>
                Abbrechen
              </Button>
              <Button type="submit" className="btn-primary" data-testid="create-user-btn">
                Erstellen
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Settings;
